import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ImageCompareModalProps {
  originalUrl: string;
  currentUrl: string;
  onClose: () => void;
}

const ZOOM_LEVELS = [
  { label: 'Fit', value: 0 }, // 0 = fit-to-pane, computed per image
  { label: '100%', value: 1 },
  { label: '200%', value: 2 },
];

// Opened via a "Compare" button next to the wizard's inline preview (see
// BackgroundColorTool.tsx) — per direct user request for a zoom option "to
// compare original and current at 100% view," this gives the two images
// real screen space side by side rather than trying to cram zoom/pan
// controls into the 240px inline preview panel, which stays fit-to-box and
// zoom-free.
export function ImageCompareModal({ originalUrl, currentUrl, onClose }: ImageCompareModalProps) {
  const { t } = useTranslation();
  const [zoomLevel, setZoomLevel] = useState(0); // index into ZOOM_LEVELS
  const paneRefs = useRef<Array<HTMLDivElement | null>>([]);
  // Natural pixel dimensions of each image, keyed by URL — needed because
  // "100%"/"200%" must mean a real percentage of the image's OWN pixel size
  // (a true 1:1 or 2:1 view), not a percentage of the pane's width. Using
  // `width: '100%'`/`'200%'` in CSS (the original, buggy implementation)
  // resolves against the CONTAINER's width instead, so "100%" just looked
  // like "Fit" (or worse) and "200%" scaled relative to the pane rather than
  // the image — reported live as "zoom 200% is not working."
  const [naturalSizes, setNaturalSizes] = useState<Record<string, { w: number; h: number }>>({});

  useEffect(() => {
    [originalUrl, currentUrl].forEach(url => {
      if (naturalSizes[url]) return;
      const img = new Image();
      img.onload = () => setNaturalSizes(prev => ({ ...prev, [url]: { w: img.naturalWidth, h: img.naturalHeight } }));
      img.src = url;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalUrl, currentUrl]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Scrolling one pane scrolls the other in lockstep, so panning while
  // zoomed past fit keeps both images aligned on the same region instead of
  // drifting apart — this is what makes "compare at 100%" actually usable
  // for a large image where only part fits in the pane at once.
  const syncScroll = (sourceIndex: number) => (e: React.UIEvent<HTMLDivElement>) => {
    const other = paneRefs.current[1 - sourceIndex];
    if (other) { other.scrollLeft = e.currentTarget.scrollLeft; other.scrollTop = e.currentTarget.scrollTop; }
  };

  const zoomValue = ZOOM_LEVELS[zoomLevel].value;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 10, width: 'min(1000px, 100%)', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <strong style={{ fontSize: 14 }}>{t('bgColor.compareTitle')}</strong>
          <div style={{ display: 'flex', gap: 4 }}>
            {ZOOM_LEVELS.map((z, i) => (
              <button
                key={z.label}
                onClick={() => setZoomLevel(i)}
                style={{
                  fontSize: 12, padding: '5px 10px', borderRadius: 5, cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: zoomLevel === i ? 'var(--selection)' : 'transparent',
                  color: zoomLevel === i ? 'white' : 'var(--text-muted)',
                }}
              >
                {z.label}
              </button>
            ))}
            <button onClick={onClose} title={t('bgColor.closeCompare')}
              style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 8px' }}>
              ×
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {[
            { label: t('bgColor.compareOriginal'), url: originalUrl },
            { label: t('bgColor.compareCurrent'), url: currentUrl },
          ].map((pane, i) => (
            <div key={pane.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: i === 0 ? '1px solid var(--border)' : undefined, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', padding: '8px 12px', textAlign: 'center', background: '#f8fafc' }}>
                {pane.label}
              </div>
              <div
                ref={el => { paneRefs.current[i] = el; }}
                onScroll={syncScroll(i)}
                style={{
                  flex: 1, overflow: 'auto', background: 'repeating-conic-gradient(#e2e8f0 0% 25%, #ffffff 0% 50%) 0 0 / 20px 20px',
                  display: 'flex', alignItems: zoomValue === 0 ? 'center' : 'flex-start', justifyContent: zoomValue === 0 ? 'center' : 'flex-start',
                }}
              >
                <img
                  src={pane.url}
                  alt={pane.label}
                  style={
                    zoomValue === 0
                      ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
                      : naturalSizes[pane.url]
                        // Real pixel width = image's own natural width × zoom
                        // factor (1 = true 100%, 2 = true 200%) — NOT a CSS
                        // percentage, which would resolve against this pane's
                        // container width instead of the image's actual size.
                        // flexShrink: 0 is load-bearing here, not decoration —
                        // the parent pane is `display: flex`, and a flex
                        // item's default flex-shrink:1 + min-width:auto
                        // SILENTLY overrides an explicit width when it doesn't
                        // fit the visible flex container, shrinking the image
                        // back down to fit instead of actually rendering wider
                        // than the pane (confirmed live: computed width came
                        // out as ~499.5px against an explicit 600px style —
                        // exactly the pane's own width, not the requested
                        // zoom size — with the pane's overflow:auto never
                        // getting a chance to make the rest scrollable,
                        // because as far as flexbox was concerned the image
                        // never actually exceeded the container).
                        ? { width: naturalSizes[pane.url].w * zoomValue, maxWidth: 'none', flexShrink: 0, imageRendering: zoomValue > 1 ? 'pixelated' : 'auto' }
                        // Natural size not loaded yet (first render) — fall back
                        // to intrinsic sizing rather than stretching to 100%/200%
                        // of the container, which is exactly the bug being fixed.
                        : { maxWidth: 'none', flexShrink: 0 }
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
